import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Nav from "./Nav";
import Footer from "./Footer";
import "./home.css";
import useFetch from "../Components/UseFetch";
import StandardPageExpress from "../Components/StandardCardExpress";
import SuccessMessage from "../Components/SuccessPopup";
import ErrorMessage from "../Components/ErrorPopup";
import { base64backendtoken } from "../HelperFx/datalinks.js";
import verifyToken from "../HelperFx/verifyJwt";

const REACT_EXPRESS = process.env.REACT_APP_EXPRESS_SERVER;

const emptyUser = {
  userName: "",
  customerNo: "",
  customerName: "",
  isAdmin: "USER",
  jobTitle: "",
  phoneNo: "",
  email: "",
  pwd: "",
  pwd2: "",
  tfa: false,
  blocked: false,
};

const authHeaders = {
  headers: {
    "Content-Type": "application/json",
    Authorization: `Basic ${base64backendtoken}`,
  },
};

const UserCard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [fdata, setData] = useState(emptyUser);
  const [recordId, setRecordId] = useState(location.state?.id || null);
  const [DocNo, setDocNo] = useState("New User");
  const [isSuccessOpen, setSuccessOpen] = useState(false);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const jwttoken = sessionStorage.getItem("authToken");
  const decoded = verifyToken(jwttoken) || {};
  const { userName: sessionUser, custName } = decoded;

  const { data: fetchedUser } = useFetch(
    recordId ? `${REACT_EXPRESS}/user/${recordId}` : null,
    base64backendtoken,
  );

  useEffect(() => {
    setRecordId(location.state?.id || null);
  }, [location.state?.id]);

  useEffect(() => {
    if (!fetchedUser) return;
    setData({
      ...emptyUser,
      ...fetchedUser,
      jobTitle: fetchedUser.jobTitle || fetchedUser.JobTitle || "",
      pwd: "",
      pwd2: "",
    });
    setDocNo(`${fetchedUser.userName || fetchedUser.UserName} - User Card`);
  }, [fetchedUser]);

  const showError = (err) => {
    const backendMessage =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "An error occurred while sending the request.";
    setPopupMessage(backendMessage);
    setErrorOpen(true);
  };

  const applySavedUser = (user) => {
    if (!user) return;
    setData({
      ...emptyUser,
      ...user,
      jobTitle: user.jobTitle || user.JobTitle || "",
      pwd: "",
      pwd2: "",
    });
    setDocNo(`${user.userName || user.UserName} - User Card`);
    if (user.userName && user.userName !== recordId) {
      setRecordId(user.userName);
      navigate("/User", { replace: true, state: { id: user.userName } });
    }
  };

  const handlePageBlur = async (field, value) => {
    if (field === "pwd2") {
      if (value && fdata.pwd && value !== fdata.pwd) {
        setPopupMessage("Passwords do not match.");
        setErrorOpen(true);
      }
      return;
    }

    if (value === fdata[field]) return;

    const nextValues = { ...fdata, [field]: value };

    if (!recordId) {
      if (!nextValues.userName || !nextValues.customerNo || !nextValues.email) {
        setData(nextValues);
        return;
      }
      if (nextValues.pwd && nextValues.pwd2 && nextValues.pwd !== nextValues.pwd2) {
        setPopupMessage("Passwords do not match.");
        setErrorOpen(true);
        setData(nextValues);
        return;
      }
      try {
        const payload = {
          userName: nextValues.userName,
          customerNo: nextValues.customerNo,
          customerName: nextValues.customerName,
          isAdmin: nextValues.isAdmin || "USER",
          jobTitle: nextValues.jobTitle,
          phoneNo: nextValues.phoneNo,
          email: nextValues.email,
          tfa: Boolean(nextValues.tfa),
          blocked: Boolean(nextValues.blocked),
        };
        if (nextValues.pwd) payload.password = nextValues.pwd;
        const response = await axios.post(
          `${REACT_EXPRESS}/users`,
          payload,
          authHeaders,
        );
        applySavedUser(response.data.user);
      } catch (err) {
        showError(err);
      }
      return;
    }

    if (field === "pwd") {
      if (!value) return;
      try {
        const response = await axios.patch(
          `${REACT_EXPRESS}/users/${recordId}`,
          { password: value },
          authHeaders,
        );
        applySavedUser(response.data.user);
      } catch (err) {
        showError(err);
      }
      return;
    }

    try {
      const response = await axios.patch(
        `${REACT_EXPRESS}/users/${recordId}`,
        { [field]: value },
        authHeaders,
      );
      applySavedUser(response.data.user);
    } catch (err) {
      showError(err);
    }
  };

  const handleActivateUser = async () => {
    if (!recordId) return;
    if (fdata.userName === sessionUser || fdata.customerNo === custName) {
      setPopupMessage("You are already actively using the system.");
      setErrorOpen(true);
      return;
    }

    try {
      const response = await axios.post(
        `${REACT_EXPRESS}/activateuser`,
        {
          userName: fdata.userName,
          customerNo: fdata.customerNo,
        },
        authHeaders,
      );
      setPopupMessage(
        response.data.message ||
          "User activated successfully. A temporary password has been sent to the user's email.",
      );
      setSuccessOpen(true);
    } catch (err) {
      showError(err);
    }
  };

  const fields = [
    { name: "userName", label: "User Name", type: "text" },
    { name: "customerNo", label: "Customer No.", type: "text" },
    { name: "customerName", label: "Customer Name", type: "text" },
    {
      name: "isAdmin",
      label: "User Type",
      type: "select",
      options: [
        { value: "USER", label: "USER" },
        { value: "ADMIN", label: "ADMIN" },
      ],
    },
    { name: "jobTitle", label: "Job Title", type: "text" },
    { name: "phoneNo", label: "Phone No", type: "text" },
    { name: "email", label: "Email", type: "text" },
    ...(!recordId
      ? [
          { name: "pwd", label: "Password", type: "password" },
          { name: "pwd2", label: "Confirm Password", type: "password" },
        ]
      : []),
    { name: "tfa", label: "Enable Two Factor Authentication", type: "checkbox" },
    { name: "blocked", label: "Blocked", type: "checkbox" },
  ];

  const actions = recordId
    ? [
        {
          name: "Activate User",
          visible: true,
          fx: handleActivateUser,
        },
      ]
    : [];

  return (
    <div className="Home">
      <Nav />
      <div className="dash-main settings-full" style={{ padding: "20px" }}>
        <StandardPageExpress
          title={DocNo}
          fields={fields}
          initialData={fdata}
          actions={actions}
          redirectTo="UserManagement"
          onBlur={handlePageBlur}
        />
      </div>
      <Footer />
      <SuccessMessage
        isOpen={isSuccessOpen}
        toggle={() => setSuccessOpen(!isSuccessOpen)}
        caption="Success"
        message={popupMessage}
      />
      <ErrorMessage
        isOpen={isErrorOpen}
        toggle={() => setErrorOpen(!isErrorOpen)}
        caption="Error"
        message={popupMessage}
      />
    </div>
  );
};

export default UserCard;
